"use client";

import type { z } from "zod/v4";
import { ApiError } from "../../api-client";
import { getClientToken, refreshClientToken } from "../../auth";

export const FINANCE_API_URL =
	process.env.NEXT_PUBLIC_FINANCE_API_URL ??
	(process.env.NEXT_PUBLIC_API_URL
		? `${process.env.NEXT_PUBLIC_API_URL}/finance`
		: "http://localhost:8085");

// `new Headers(init)` accepts a Headers instance, a tuple list, or a record;
// spreading a Headers instance yields {} and silently drops every header.
function authHeaders(init?: HeadersInit): Headers {
	const headers = new Headers(init);
	if (!headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}
	const token = getClientToken();
	if (token && !headers.has("Authorization")) {
		headers.set("Authorization", `Bearer ${token}`);
	}
	return headers;
}

async function fetchWithRetry(url: string, options?: RequestInit): Promise<Response> {
	const res = await fetch(url, {
		...options,
		headers: authHeaders(options?.headers),
	});

	if (res.status === 401) {
		const newToken = await refreshClientToken();
		if (newToken) {
			return fetch(url, {
				...options,
				headers: authHeaders(options?.headers),
			});
		}
	}

	return res;
}

export async function fetchApi<T>(
	path: string,
	schema: z.ZodType<T>,
	options?: RequestInit,
): Promise<T> {
	const res = await fetchWithRetry(`${FINANCE_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	const json = await res.json();
	return schema.parse(json);
}

export async function fetchVoid(path: string, options?: RequestInit): Promise<void> {
	const res = await fetchWithRetry(`${FINANCE_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}
}

export async function fetchFormData<T>(
	path: string,
	schema: z.ZodType<T>,
	formData: FormData,
): Promise<T> {
	const headers: Record<string, string> = {};
	const token = getClientToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	const res = await fetch(`${FINANCE_API_URL}${path}`, {
		method: "POST",
		headers,
		body: formData,
	});

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	const json = await res.json();
	return schema.parse(json);
}
